import Image from "next/image";
import { useRouter } from "next/router";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import { useStaggerAnimation } from "@/hooks/useStaggerAnimation";
import SectionHeading from "@/components/home/SectionHeading";

/**
 * 구매 혜택 "부담없이 시작하세요" — 피그마 S6 Benefits(28983:88616).
 *
 * 시안에서 미디어 처리가 셋으로 갈린다.
 *   - light : Cool Gray/10 카드 + 250x96 배지
 *   - dark  : 검정 카드 + 153x180 배지
 *   - bleed : 카드 전체를 채우는 이미지(자체 배경 포함)
 * 이전 구현처럼 3장을 같은 회색 카드에 `object-contain` 으로 얹으면 안 된다.
 *
 * ⚠️ **카드마다 축이 다르다. 통일하지 말 것.**
 *
 *   - 어떤 카드를 보여줄지 = **배송 국가**(`countryCode`). 무료 배송은 국내 배송 정책이라
 *     언어가 아니라 국가로 갈려야 한다.
 *   - 환불 일수 표기 = **locale**. `docs/refund-policy.md` 가 "표기 축은 locale, 집행 축만
 *     `order.shippingCountry`" 로 규정한다.
 *
 * ⚠️ 환불 배지 이미지에는 일수가 path 로 **그려져** 있어(텍스트 노드 0개) 번역 레이어가
 * 손댈 수 없다. 그래서 파일을 갈아끼우는데, **그 선택도 문구와 같은 locale 축이어야 한다.**
 * 한때 `countryCode` 로 골랐다가 `/ko` + `bh_ccode=US` 에서 문구는 "14일 전액 환불",
 * 배지는 `45 Day` 로 어긋났다(실측). 카드 하나 안에서 일수가 두 개로 보이는 상태였다.
 *
 * 디자이너 프로토타입·국문 보드 모두 `45 Day` SVG 를 재사용하고 있는데 이는 시안 쪽 실수다
 * (KR = 14일). KR 배지는 아직 SVG 원본이 없어 기존 PNG 를 잘라 쓰는 중.
 *
 * ⚠️ 문구는 **왼쪽 정렬**이다(피그마 Group 28983:91490 이하). 이전 구현은 가운데였다.
 */

const BASE_PATH = "/images/home/main-renewal";

type Benefit = {
  key: string;
  media: "light" | "dark" | "bleed";
  image: string;
  /**
   * light/dark 카드에서 배지가 차지하는 **비율**(미디어 폭 대비).
   *
   * ⚠️ 고정 px 이 아니다. 모바일 보드의 배지는 데스크톱의 정확히 70.69%(=328/464)로,
   * 카드 폭에 비례해 줄어든다. 250px 로 박아두면 모바일에서 1.4배 크게 나온다.
   * 세로는 이미지 고유 비율이 따라오므로 가로만 지정한다.
   */
  widthRatio?: string;
  intrinsic?: { width: number; height: number };
  alt: string;
  title: string;
  description: string;
};

// 국문 보드(29007:93277 `Cover`)는 이 카드를 464x342 SVG 한 장으로 준다 —
// 배경(Blue/10)과 라운드까지 아트에 들어 있어 bleed 로 그대로 깐다.
//
// ⚠️ 이 파일만 `main-renewal` 이 아니라 `common` 에 있다 — **PDP 혜택 카드와 공유**한다
// (`ProductFeatureSections.tsx` 의 한국 전용 무료배송 카드, #5287). 같은 그림을 두 벌 두지
// 않으려고 재사용하는 것이니 홈 리뉴얼 자산을 정리할 때 같이 지우지 말 것.
// 프레임(464x342)을 바꿔 재출력하면 PDP 쪽 `object-cover` 크롭이 달라진다 —
// `__tests__/components/benefit-shipping-image.test.ts` 가 경로·프레임·배경을 잠근다.
const FREE_SHIPPING_KR: Benefit = {
  key: "shipping",
  media: "bleed",
  image: "/images/common/benefit-shipping.svg",
  alt: "Free Shipping",
  title: "Free Shipping",
  description: "Free shipping across Korea.",
};

const FREE_SOFTWARE: Benefit = {
  key: "software",
  media: "bleed",
  image: `${BASE_PATH}/benefit-software.png`,
  alt: "bHaptics Player",
  title: "Free Software Updates",
  description: "Haptic experiences keep improving.",
};

const WARRANTY: Benefit = {
  key: "warranty",
  media: "dark",
  image: `${BASE_PATH}/benefit-warranty.svg`,
  // 153 / 464
  widthRatio: "32.97%",
  intrinsic: { width: 153, height: 180 },
  alt: "1-Year Warranty",
  title: "1-Year Global Warranty",
  description: "Covered with global support.",
};

/** ⚠️ 인자는 배송 국가가 아니라 **locale** 이다 — 바로 옆 문구(`t()`)와 같은 축이어야 한다. */
const refundBenefit = (isKoreanLocale: boolean): Benefit => ({
  key: "refund",
  media: "light",
  image: isKoreanLocale
    ? `${BASE_PATH}/benefit-refund-kr.png`
    : `${BASE_PATH}/benefit-refund.svg`,
  // 250 / 464
  widthRatio: "53.88%",
  intrinsic: { width: 250, height: 96 },
  alt: "Money Back Guarantee",
  title: "45-Day Refund Policy",
  description: "Try it and decide with confidence.",
});

export default function StartWithConfidence({
  countryCode,
  className,
}: {
  countryCode?: string;
  className?: string;
}) {
  const { t } = useTrans(["index"]);
  const { ref, className: delayClassName, delayFunc } = useStaggerAnimation({});

  // 카드 구성 = 배송 국가 축 / 환불 배지 = locale 축. 위 docblock 참조.
  const isKR = countryCode === "KR";
  const isKoreanLocale = useRouter().locale === "ko";
  const benefits: Benefit[] = isKR
    ? [FREE_SHIPPING_KR, refundBenefit(isKoreanLocale), WARRANTY]
    : [refundBenefit(isKoreanLocale), WARRANTY, FREE_SOFTWARE];

  return (
    <section className={cn("w-full px-4 laptop:px-10", className)}>
      <div className="flex flex-col items-center">
        <SectionHeading title={t("Start with Confidence")} />

        {/* 시안의 행은 1456(464x3 + 32x2)으로 1440 컨테이너보다 넓다 — max-w 를 따로 준다. */}
        <div
          ref={ref}
          className="mt-6 flex w-full max-w-[1456px] flex-col gap-5 laptop:mt-8 laptop:flex-row laptop:justify-center laptop:gap-8"
        >
          {benefits.map((benefit, index) => (
            <article
              key={benefit.key}
              style={{ transitionDelay: delayFunc(index) }}
              className={cn(
                "flex w-full flex-col gap-4 laptop:max-w-[464px] laptop:flex-1 laptop:gap-5",
                delayClassName,
              )}
            >
              {/* 미디어 영역은 어느 폭에서든 464:342 를 유지한다 — 모바일 보드의 328x241.76 이
                  같은 비율이다. 고정 높이로 두면 360 보다 넓은 기기(예: 402)에서 어긋난다. */}
              <div
                className={cn(
                  "flex aspect-[464/342] w-full items-center justify-center overflow-hidden rounded-2xl",
                  benefit.media === "light" && "bg-coolgray-10",
                  benefit.media === "dark" && "bg-black",
                )}
              >
                {/* ⚠️ SVG 는 next/image 옵티마이저가 처리하지 않는다(`dangerouslyAllowSVG` 미설정).
                    `unoptimized` 를 빼면 그 카드만 이미지가 통째로 안 나온다 — bleed/badge 양쪽
                    모두 svg 가 들어올 수 있으므로 두 분기에 다 붙인다. */}
                {benefit.media === "bleed" ? (
                  <Image
                    src={benefit.image}
                    alt={t(benefit.alt)}
                    width={928}
                    height={684}
                    sizes="(max-width: 1023px) 100vw, 464px"
                    unoptimized={benefit.image.endsWith(".svg")}
                    className="size-full object-cover"
                  />
                ) : (
                  <Image
                    src={benefit.image}
                    alt={t(benefit.alt)}
                    width={benefit.intrinsic?.width ?? 250}
                    height={benefit.intrinsic?.height ?? 96}
                    sizes="(max-width: 1023px) 50vw, 250px"
                    unoptimized={benefit.image.endsWith(".svg")}
                    // 세로는 고유 비율이 따라온다 — 가로 비율만 지정한다.
                    style={{ width: benefit.widthRatio ?? "53.88%" }}
                    className="h-auto object-contain"
                  />
                )}
              </div>

              <div className="flex flex-col gap-2 laptop:gap-3">
                <h3 className="text-2xl font-400 text-coolgray-100">
                  {t(benefit.title)}
                </h3>
                <p className="whitespace-pre-wrap text-base tracking-[0.25px] text-coolgray-80">
                  {t(benefit.description)}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
